import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  GsdAppShell,
  HelpDrawer,
  HelpProvider,
  useHelp,
  injectCoquilleTokens,
} from "@/coquille-bridge";
import { PasswordField } from "@/components/PasswordField.jsx";
import { orignalAppConfig } from "@/config/orignalAppConfig.js";
import { resolveHelpScreenId } from "@/config/resolveHelpScreen.js";
import { getHelpFallbackTopic, getHelpTopicRegistry } from "@/content/helpContent.js";
import { useAuth } from "@/context/AuthContext.jsx";
import { useOrignalUi } from "@/hooks/useOrignalUi.js";
import { api, apiJson } from "@/api/client.js";
import { AppBackground } from "@/components/AppBackground.jsx";
import { PWSVersionBar } from "@/components/PWSVersionBar.jsx";
import { TopNav } from "@/layout/TopNav.jsx";

injectCoquilleTokens();

function HelpDrawerConnected() {
  const { open, topic, closeHelp } = useHelp();
  const { ui } = useOrignalUi();
  return (
    <HelpDrawer
      open={open}
      onClose={closeHelp}
      topic={topic}
      testId="help-drawer"
      closeLabel={ui.help.close}
      labels={{ close: ui.help.close }}
    />
  );
}

export function AppLayout() {
  const { isAuthenticated, name, role, logout, user, refreshMe } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { lang, ui } = useOrignalUi();
  const screenId = resolveHelpScreenId(location.pathname);
  const topicRegistry = useMemo(() => getHelpTopicRegistry(lang), [lang]);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [pwdMsg, setPwdMsg] = useState("");
  const mapFull = location.pathname.startsWith("/territoire");

  useEffect(() => {
    if (location.pathname === "/" || location.pathname === "/aujourdhui") {
      api("/api/me/ack-visit", { method: "POST" }).catch(() => {});
    }
  }, [location.pathname]);

  const navGroups = useMemo(
    () => [
      {
        id: "main",
        label: lang === "en" ? "Main" : "Principal",
        items: [],
      },
    ],
    [lang, ui],
  );

  return (
    <HelpProvider
      pageContext={{ screenId }}
      topicRegistry={topicRegistry}
      fallbackTopic={getHelpFallbackTopic(lang)}
    >
      <div className={`og-root og-root--cockpit${mapFull ? " is-map" : ""}`} data-surface="dark" data-testid="orignal-background-shell">
        <AppBackground />
        <TopNav
          onLogout={() => {
            logout();
            navigate("/connexion");
          }}
        />
        <GsdAppShell
          config={orignalAppConfig}
          locale={lang}
          activeRole={role || "member"}
          isAuthenticated={isAuthenticated}
          userDisplayName={name}
          version="0.71.0"
          testId="gsd-app-shell"
          layoutVariant="top-nav"
          shellMode="auth"
          navGroups={navGroups}
          mobileNavItems={[]}
          hideHeaderNav
          onLogout={() => {
            logout();
            navigate("/connexion");
          }}
          renderNavLink={(item, meta) => (
            <NavLink to={item.to} end={item.end} className={meta.className} data-testid={`shell-nav-${item.id}`}>
              {meta.children}
            </NavLink>
          )}
          isItemActive={(item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)}
        >
          <div className="og-shell">
            <Outlet />
          </div>
        </GsdAppShell>
        {user?.must_change_password ? (
          <div className="og-modal" data-testid="password-modal">
            <form
              className="og-modal__card og-form"
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await apiJson("/api/auth/change-password", { current_password: current, new_password: next });
                  await refreshMe();
                  setPwdMsg(ui.app.passwordChanged);
                } catch {
                  setPwdMsg(ui.errors.login);
                }
              }}
            >
              <h2>{ui.app.changePassword}</h2>
              <PasswordField label={ui.app.currentPassword} value={current} onChange={setCurrent} showLabel={ui.app.showPassword} hideLabel={ui.app.hidePassword} />
              <PasswordField label={ui.app.newPassword} value={next} onChange={setNext} autoComplete="new-password" showLabel={ui.app.showPassword} hideLabel={ui.app.hidePassword} />
              {pwdMsg ? <p className="og-hint">{pwdMsg}</p> : null}
              <button className="og-btn og-btn--primary" type="submit">{ui.app.save}</button>
            </form>
          </div>
        ) : null}
        <HelpDrawerConnected />
        <PWSVersionBar />
      </div>
    </HelpProvider>
  );
}
